import { BusEventBase, BusEventWithPayload } from '@grafana/data';
import { getAppEvents, reportInteraction } from '@grafana/runtime';
import type { ChatContextItem } from './context/types';
import { ASSISTANT_PLUGIN_ID, ASSISTANT_PLUGIN_TITLE } from './plugin';

interface OpenExtensionSidebarPayload {
  props?: Record<string, unknown>;
  pluginId: string;
  componentTitle: string;
}

class OpenExtensionSidebarEvent extends BusEventWithPayload<OpenExtensionSidebarPayload> {
  static type = 'open-extension-sidebar';
}

class CloseExtensionSidebarEvent extends BusEventBase {
  static type = 'close-extension-sidebar';
}

class ToggleExtensionSidebarEvent extends BusEventWithPayload<OpenExtensionSidebarPayload> {
  static type = 'toggle-extension-sidebar';
}

function openExtensionSidebar(pluginId: string, componentTitle: string, props?: Record<string, unknown>) {
  const event = new OpenExtensionSidebarEvent({
    pluginId,
    componentTitle,
    props,
  });
  getAppEvents().publish(event);
}

function closeExtensionSidebar() {
  const event = new CloseExtensionSidebarEvent();
  getAppEvents().publish(event);
}

function toggleExtensionSidebar(pluginId: string, componentTitle: string, props?: Record<string, unknown>) {
  const event = new ToggleExtensionSidebarEvent({
    pluginId,
    componentTitle,
    props,
  });
  getAppEvents().publish(event);
}

export type OpenAssistantProps = {
  origin: string;

  prompt?: string;
  context?: ChatContextItem[];
  autoSend?: boolean;
  /** Mode to open the assistant in. Built-in modes: 'assistant', 'investigation', 'dashboarding'. Can also be a backend agent ID. */
  mode?: string;
  /** When set, sends the prompt as a follow-up into this existing conversation instead of creating a new one. */
  chatId?: string;
  /** When true, append context to the chat instead of replacing it. */
  appendContext?: boolean;
};

/**
 * Open the Grafana Assistant sidebar with a given initial prompt.
 *
 * @param props - The props to pass to the assistant.
 * @param props.origin - The origin of the request that opened the assistant. This is used to track the source of the request. Should be a structured string using forward slashes, with the first part as a namespace. Examples: 'grafana-datasources/prometheus/query-builder', 'grafana-slo-app/slo-editor-overview', 'grafana/trace-view-analyzer`.
 * @param props.prompt - The initial prompt to display in the assistant.
 * @param props.context - The initial context to display in the assistant. Created with `createContext`.
 * @param props.autoSend - Whether to automatically send the initial prompt. When true, opens a chat and sends the initial prompt right away. When false, opens chat and updates user message without sending it. Defaults to true.
 * @param props.mode - The mode to open the assistant in. Built-in modes: 'assistant', 'investigation', 'dashboarding'. Can also be a backend agent ID. Defaults to 'assistant'.
 * @param props.chatId - When set, sends the prompt as a follow-up into this existing conversation instead of creating a new one.
 */
export function openAssistant(props: OpenAssistantProps) {
  reportInteraction('grafana_assistant_app_opened_sidebar', {
    from: props.origin,
  });

  openExtensionSidebar(ASSISTANT_PLUGIN_ID, ASSISTANT_PLUGIN_TITLE, {
    initialPrompt: props.prompt,
    initialContext: props.context,
    initialAutoSend: props.autoSend ?? true,
    origin: props.origin,
    initialMode: props.mode,
    chatId: props.chatId,
    appendContext: props.appendContext,
  });
}

/**
 * Close the Grafana Assistant sidebar.
 */
export function closeAssistant() {
  closeExtensionSidebar();
}

export function toggleAssistant(props: OpenAssistantProps) {
  reportInteraction('grafana_assistant_app_toggled_sidebar', {
    from: props.origin,
    prompt: props.prompt ?? '',
  });

  toggleExtensionSidebar(ASSISTANT_PLUGIN_ID, ASSISTANT_PLUGIN_TITLE, {
    initialPrompt: props.prompt,
    initialContext: props.context,
    initialAutoSend: props.autoSend ?? true,
    origin: props.origin,
    initialMode: props.mode,
    chatId: props.chatId,
    appendContext: props.appendContext,
  });
}
